import { useCallback } from 'react';
import { decodeBrackets } from '../utils/queryUtils';

/**
 * Returns a memoized callback that sets (or updates) a filter query param
 * and performs a hard reload so all `useEffect` data-fetches run fresh.
 *
 * Existing params are preserved, history is kept via `assign`.
 * If `value` is empty the page reloads as-is (current URL preserved).
 *
 * @param key - Query param name (e.g. `ip_address`).
 * @param value - Query param value (e.g. `77.77.77.77`). Pass `''` while the value is not yet available - the page will
 * reload without modifying the URL.
 *
 * @example
 * const applyFilter = useApplyFilterParam('ip_address', '77.77.77.77');
 * call applyFilter() in an event handler
 */
const useApplyFilterParam = (key: string, value: string): (() => void) => {
  return useCallback(() => {
    const { pathname, search } = window.location;

    if (!value) {
      window.location.reload();
      return;
    }

    const params = new URLSearchParams(search);
    params.set(`filter[${key}]`, value);
    window.location.assign(`${pathname}?${decodeBrackets(params.toString())}`);
  }, [key, value]);
};

export default useApplyFilterParam;
